import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useEnabledQuery } from '../../../../ui/hooks/useEnabledQuery';
import { getMutationSchema } from '../../../../ui/util/getMutationSchema';
import { ResourceType } from '../../../enumerations/ResourceType';
import { get } from '../../../resource-base/APIVersion1';
import { useIndirectlyVersionedResourceAdapter } from '../../../resource-base/adapters/useIndirectlyVersionedResourceAdapter';
import { useMultiResourceSingleParentedResourceAdapterActions } from '../../../resource-base/adapters/useMultiResourceSingleParentedResourceAdapterActions';
import {
    defaultIndirectlyVersionedAndNamedArtifactResource,
    IndirectlyVersionedAndNamedArtifactResourceSchema,
} from '../../../resource-base/artifactResourceTypes';
import { ParamConstants } from '../../../resource-base/types';

export enum ImageAttachmentPropertyValidation {
    NAME = 'NAME',
    DESCRIPTION = 'DESCRIPTION',
    FILE_NAME = 'FILE_NAME',
    FILE_CONTENT = 'FILE_CONTENT',
}

export const ImageAttachmentResourceSchema = IndirectlyVersionedAndNamedArtifactResourceSchema.extend({
    resourceType: z.literal(ResourceType.IMAGE_ATTACHMENT).default(ResourceType.IMAGE_ATTACHMENT),
    description: z.string(),
    fileName: z.string(),
    fileContent: z.string(),
});
export const ImageAttachmentMutationResourceSchema = getMutationSchema(ImageAttachmentResourceSchema);

export type ImageAttachmentResource = z.infer<typeof ImageAttachmentResourceSchema>;

export const defaultImageAttachmentResource: ImageAttachmentResource = {
    ...defaultIndirectlyVersionedAndNamedArtifactResource,
    resourceType: ResourceType.IMAGE_ATTACHMENT,
    description: '',
    fileName: '',
    fileContent: '',
};

export function useImageAttachmentActions() {
    const getEnabled = useEnabledQuery();
    const actions = {
        ...useMultiResourceSingleParentedResourceAdapterActions(
            'project/image-attachment',
            ResourceType.IMAGE_ATTACHMENT,
            ImageAttachmentResourceSchema
        ),
        ...useIndirectlyVersionedResourceAdapter(
            'project/image-attachment',
            ResourceType.IMAGE_ATTACHMENT,
            ImageAttachmentResourceSchema
        ),
    };

    const getAllResourcesByExtract = (parentId: string): Promise<Array<ImageAttachmentResource> | null> => {
        return get(`project/image-attachment/extracts?${ParamConstants.PARENT_ID_REQUEST_PARAM_NAME}=${parentId}`).then(
            (result) => actions.processGetCollectionResponse<ImageAttachmentResource>(result)
        );
    };

    const useGetAllResourcesByExtract = (parentId: string | undefined | null) => {
        return useQuery({
            queryKey: [parentId, ResourceType.IMAGE_ATTACHMENT, 'Extracts'],
            queryFn: () => getAllResourcesByExtract(parentId as string),
            enabled: getEnabled(parentId),
        });
    };

    return {
        ...actions,
        useGetAllResourcesByExtract,
    };
}
